export type GdRichTextFormat = "bold" | "italic" | "underline";

const FORMAT_MARKERS: Record<GdRichTextFormat, { prefix: string; suffix: string }> = {
  bold: { prefix: "**", suffix: "**" },
  italic: { prefix: "*", suffix: "*" },
  underline: { prefix: "++", suffix: "++" },
};

export type RichTextSegment =
  | { type: "text"; value: string }
  | { type: "bold"; children: RichTextSegment[] }
  | { type: "italic"; children: RichTextSegment[] }
  | { type: "underline"; children: RichTextSegment[] };

function findNextMarkerIndex(text: string, from: number): number {
  let best = text.length;
  const bold = text.indexOf("**", from);
  if (bold !== -1) best = Math.min(best, bold);
  const underline = text.indexOf("++", from);
  if (underline !== -1) best = Math.min(best, underline);
  const italic = text.indexOf("*", from);
  if (italic !== -1) best = Math.min(best, italic);
  return best;
}

export function tokenizeGdRichText(text: string): RichTextSegment[] {
  const result: RichTextSegment[] = [];
  let i = 0;
  while (i < text.length) {
    if (text.startsWith("**", i)) {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        result.push({ type: "bold", children: tokenizeGdRichText(text.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if (text.startsWith("++", i)) {
      const end = text.indexOf("++", i + 2);
      if (end !== -1) {
        result.push({ type: "underline", children: tokenizeGdRichText(text.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "*") {
      const end = text.indexOf("*", i + 1);
      if (end !== -1 && end > i + 1) {
        result.push({ type: "italic", children: tokenizeGdRichText(text.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
    }
    const j = findNextMarkerIndex(text, i + 1);
    result.push({ type: "text", value: text.slice(i, j) });
    i = j;
  }
  return result;
}

export function stripGdRichTextMarkup(text: string): string {
  if (!text) return "";
  let out = "";
  let i = 0;
  while (i < text.length) {
    if (text.startsWith("**", i)) {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        out += stripGdRichTextMarkup(text.slice(i + 2, end));
        i = end + 2;
        continue;
      }
    }
    if (text.startsWith("++", i)) {
      const end = text.indexOf("++", i + 2);
      if (end !== -1) {
        out += stripGdRichTextMarkup(text.slice(i + 2, end));
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "*") {
      const end = text.indexOf("*", i + 1);
      if (end !== -1 && end > i + 1) {
        out += stripGdRichTextMarkup(text.slice(i + 1, end));
        i = end + 1;
        continue;
      }
    }
    const j = findNextMarkerIndex(text, i + 1);
    out += text.slice(i, j);
    i = j;
  }
  return out;
}

export function wrapGdRichTextSelection(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  format: GdRichTextFormat,
): { value: string; selectionStart: number; selectionEnd: number } {
  const { prefix, suffix } = FORMAT_MARKERS[format];
  const before = value.slice(0, selectionStart);
  const selected = value.slice(selectionStart, selectionEnd);
  const after = value.slice(selectionEnd);
  if (!selected) {
    return { value, selectionStart, selectionEnd };
  }
  const nextValue = `${before}${prefix}${selected}${suffix}${after}`;
  const nextStart = selectionStart + prefix.length;
  const nextEnd = nextStart + selected.length;
  return { value: nextValue, selectionStart: nextStart, selectionEnd: nextEnd };
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function segmentsToDisplayHtml(segments: RichTextSegment[]): string {
  return segments
    .map((segment) => {
      if (segment.type === "text") return escapeHtml(segment.value);
      const inner = segmentsToDisplayHtml(segment.children);
      if (segment.type === "bold") return `<strong>${inner}</strong>`;
      if (segment.type === "italic") return `<em>${inner}</em>`;
      return `<u>${inner}</u>`;
    })
    .join("");
}

/** Markdown mit Sichtbarkeits-Markern in HTML für contenteditable. */
export function gdRichTextToDisplayHtml(text: string, multiline = true): string {
  if (!text) return "";
  if (!multiline) return segmentsToDisplayHtml(tokenizeGdRichText(text));
  return text
    .split("\n")
    .map((line) => (line ? segmentsToDisplayHtml(tokenizeGdRichText(line)) : ""))
    .join("<br>");
}

function wrapFormattedMarkdown(inner: string, tag: string): string {
  if (!inner) return "";
  if (tag === "strong" || tag === "b") return `**${inner}**`;
  if (tag === "em" || tag === "i") return `*${inner}*`;
  if (tag === "u") return `++${inner}++`;
  return inner;
}

const BLOCK_TAGS = new Set([
  "div",
  "p",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "tr",
  "section",
  "article",
]);

const FORMAT_TAGS = new Set(["strong", "b", "em", "i", "u"]);

/** Testbares Zwischenformat für contenteditable → Markdown. */
export type GdRichSerialNode =
  | { t: "text"; v: string }
  | { t: "br" }
  | { t: "el"; tag: string; kids: GdRichSerialNode[] };

function nodeToSerial(node: Node): GdRichSerialNode | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return { t: "text", v: (node.textContent || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n") };
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "br") return { t: "br" };
  const kids: GdRichSerialNode[] = [];
  for (const child of Array.from(el.childNodes)) {
    const serial = nodeToSerial(child);
    if (serial) kids.push(serial);
  }
  return { t: "el", tag, kids };
}

/**
 * Wandelt Browser-DOM (div/p/br aus Enter) in Markdown mit echten Zeilenumbrüchen.
 * Chrome erzeugt nach Enter oft `Text<div>nächste Zeile</div>` — ohne Block-Logik
 * würden die Absätze beim Speichern zu einer Zeile zusammenfallen.
 */
export function serializeGdRichNodes(nodes: GdRichSerialNode[]): string {
  let out = "";
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (!node) continue;
    if (node.t === "text") {
      out += node.v;
      continue;
    }
    if (node.t === "br") {
      out += "\n";
      continue;
    }
    const inner = serializeGdRichNodes(node.kids);
    if (FORMAT_TAGS.has(node.tag)) {
      out += wrapFormattedMarkdown(inner, node.tag);
      continue;
    }
    if (BLOCK_TAGS.has(node.tag)) {
      if (out.length > 0 && !out.endsWith("\n")) out += "\n";
      out += inner;
      const hasFollowing = i < nodes.length - 1;
      if (hasFollowing && !out.endsWith("\n")) out += "\n";
      continue;
    }
    out += inner;
  }
  return out;
}

/** HTML aus contenteditable zurück in Markdown mit Sichtbarkeits-Markern. */
export function gdRichTextFromDisplayElement(root: HTMLElement, multiline = true): string {
  const nodes: GdRichSerialNode[] = [];
  for (const child of Array.from(root.childNodes)) {
    const serial = nodeToSerial(child);
    if (serial) nodes.push(serial);
  }
  const raw = serializeGdRichNodes(nodes);
  if (!multiline) return raw.replace(/\n/g, " ").trim();
  return raw.replace(/\n+$/g, "");
}
